import {
  createColorThresholdFromParsed,
  createMeasurementRangeFromParsed,
  parseColorThresholdsFromMetadata,
  parseFilterRangesFromMetadata,
  parseSelectedCdfIdsFromMetadata,
} from 'features/richText/utils/unitConfigMetadataUtils'
import { useCallback, useMemo } from 'react'
import {
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoComponentDefaults,
  GeckoMeasurementRange,
} from 'types'

/**
 * Default scene configuration for AssetViz in slash command cards
 */
export const DEFAULT_SCENE_CONFIG = {
  showFeatures: true,
  showFeatureLabels: false,
  show3D: true,
} as const

interface UseSlashCommandAssetVizDataOptions {
  componentDataFrames: readonly GeckoComponentDataFrame[]
  componentDatasets: readonly GeckoComponentDataset[]
  componentDefaults: readonly GeckoComponentDefaults[]
  metadata?: Record<string, string | null | undefined>
  /**
   * Optional filter function to filter CDFs before processing
   * (e.g., to filter by inspection ID for inspections)
   */
  filterCDFs?: (cdf: GeckoComponentDataFrame) => boolean
}

/**
 * Shared hook for Unit and Inspection slash command cards
 * Handles data filtering, metadata parsing, and callback generation for AssetViz
 */
export const useSlashCommandAssetVizData = ({
  componentDataFrames,
  componentDatasets,
  componentDefaults,
  metadata,
  filterCDFs,
}: UseSlashCommandAssetVizDataOptions) => {
  const filteredCDFs = useMemo(() => {
    return filterCDFs
      ? componentDataFrames.filter(filterCDFs)
      : componentDataFrames
  }, [componentDataFrames, filterCDFs])

  const latestComponentDataFrames = useMemo(() => {
    const cdfsByComponent = new Map<string, GeckoComponentDataFrame[]>()

    filteredCDFs.forEach((cdf) => {
      const componentId = cdf.tags.component
      if (!componentId) return

      if (!cdfsByComponent.has(componentId)) {
        cdfsByComponent.set(componentId, [])
      }
      cdfsByComponent.get(componentId)?.push(cdf)
    })

    const latestCDFs: GeckoComponentDataFrame[] = []
    cdfsByComponent.forEach((cdfs) => {
      const sorted = [...cdfs].sort((a, b) => {
        return GeckoComponentDataFrame.DefaultSort(a, b, componentDatasets)
      })
      if (sorted.length > 0) {
        latestCDFs.push(sorted[0])
      }
    })

    return latestCDFs
  }, [filteredCDFs, componentDatasets])

  const savedSelectedCdfIds = useMemo(
    () => parseSelectedCdfIdsFromMetadata(metadata),
    [metadata]
  )

  const savedColorThresholds = useMemo(
    () => parseColorThresholdsFromMetadata(metadata),
    [metadata]
  )

  const savedFilterRanges = useMemo(
    () => parseFilterRangesFromMetadata(metadata),
    [metadata]
  )

  const displayedComponentDataFrames = useMemo(() => {
    if (savedSelectedCdfIds.size === 0) {
      return latestComponentDataFrames
    }

    return latestComponentDataFrames.map((cdf) => {
      const componentId = cdf.tags.component
      if (!componentId) return cdf

      const selectedId = savedSelectedCdfIds.get(componentId)
      if (!selectedId) return cdf

      const selectedCdf = componentDataFrames.find((c) => c.id === selectedId)
      return selectedCdf || cdf
    })
  }, [savedSelectedCdfIds, latestComponentDataFrames, componentDataFrames])

  const getColorThresholdForComponent = useCallback(
    (cdf: GeckoComponentDataFrame) => {
      const componentId = cdf.tags.component
      if (!componentId) return undefined

      const key = `${componentId}:${cdf.data.params.view.id}`

      if (savedColorThresholds) {
        const savedThreshold = savedColorThresholds[key]
        if (savedThreshold) {
          return createColorThresholdFromParsed(savedThreshold)
        }
      }

      const defaults = componentDefaults.find(
        (cd) => cd.tags.component === componentId
      )
      if (!defaults) {
        return undefined
      }

      return defaults.data.color_scale_thresholds.get(cdf.data.params.view.id)
    },
    [savedColorThresholds, componentDefaults]
  )

  const getFilteredValueRange = useCallback(
    (cdf: GeckoComponentDataFrame): GeckoMeasurementRange | undefined => {
      const componentId = cdf.tags.component
      if (!componentId) return undefined

      const key = `${componentId}:${cdf.data.params.view.id}`

      if (savedFilterRanges) {
        const savedRange = savedFilterRanges[key]
        if (savedRange) {
          return createMeasurementRangeFromParsed(savedRange)
        }
      }

      return undefined
    },
    [savedFilterRanges]
  )

  return {
    latestComponentDataFrames,
    displayedComponentDataFrames,
    savedSelectedCdfIds,
    savedColorThresholds,
    savedFilterRanges,
    getColorThresholdForComponent,
    getFilteredValueRange,
  }
}
